import { getField, updateField } from 'vuex-map-fields'
import {HTTP_HYPER_ESPACE_BACKEND} from "@/services/http-common"

const getDefaultFilter = () => ({
  page: 1,
  offset: 0,
  limit: 10,
  sort: '-DC',
  search: '',
  stock: null,
  annonceParente: null,
  bailleurType: null,
  typeObjets: null,
  annoncesVendues: false,
  annoncesActivees: true,
  preset: 'enable'
})

const getPresetFilters = () => ({
  preset: 'enable'
})

export default {
  namespaced: true,

  // 📊 STATE ULTRA-SIMPLIFIÉ
  state: {
    // Données principales
    filter: getDefaultFilter(),
    results: { total: -1, list: [] },
    portal: null,

    // États de chargement
    loading: false,
    loadingActivation: false,

    // Cache des dernières données reçues du backend optimisé
    dashboardCache: null,

    // Gestion des erreurs
    lastError: null
  },

  getters: {
    // Getters de base
    isLoading: state => state.loading,
    isLoadingActivation: state => state.loadingActivation,
    getCurrentPortal: state => state.portal,
    getLastError: state => state.lastError,

    // Getters des données du dashboard
    getQuotaCurrentUser: state => {
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.userQuota) ? state.dashboardCache.data.userQuota.quota : 'Aucun Quota'
    },

    getSelectedAdsCurrentUser: (state, getters, rootState, rootGetters) => {
      // Pour les promoteurs : somme programmes + lots
      const sitimmoUser = rootGetters.getSitimmoUser

      if (sitimmoUser && sitimmoUser.metier === 'PROMOTEUR') {
        const promoteurData = getters.getPromoteurData
        return promoteurData ? ((promoteurData.nbAnnoncesSelectionneesProgrammes || 0) + (promoteurData.nbAnnoncesSelectionneesLots || 0)) : 0
      }

      // Pour les bailleurs sociaux : annonces + programmes + lots
      if (sitimmoUser && sitimmoUser.metier === 'BAILLEUR_SOCIAL') {
        const promoteurData = getters.getPromoteurData
        const baseAnnonces = (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.userQuota) ? state.dashboardCache.data.userQuota.annoncesSelectionnees : 0
        return baseAnnonces + (promoteurData ? ((promoteurData.nbAnnoncesSelectionneesProgrammes || 0) + (promoteurData.nbAnnoncesSelectionneesLots || 0)) : 0)
      }

      // Pour les autres métiers : données standard
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.userQuota) ? state.dashboardCache.data.userQuota.annoncesSelectionnees : 0
    },

    getPromoteurData: state => {
      return (state.dashboardCache && state.dashboardCache.data) ? state.dashboardCache.data.promoteurData : {
        nbProgrammesTotal: 0,
        nbLotsTotal: 0,
        nbProgrammesActifs: 0,
        nbLotsActifs: 0,
        nbAnnoncesSelectionneesProgrammes: 0,
        nbAnnoncesSelectionneesLots: 0
      }
    },

    getQuotaData: state => {
      return (state.dashboardCache && state.dashboardCache.data) ? state.dashboardCache.data.quotaData : {
        quotaForUserPorPortal: '',
        quotaPersonnel: false,
        totalAnnoncesForUser: 0,
        annonceSelectionneeForPortail: 0
      }
    },

    // Getters détaillés pour l'UI
    getQuotaPersonnel: state => {
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.quotaData)
        ? state.dashboardCache.data.quotaData.quotaPersonnel || false
        : false
    },

    getQuotaForUserPorPortal: state => {
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.quotaData)
        ? state.dashboardCache.data.quotaData.quotaForUserPorPortal || ''
        : ''
    },

    getTotalAnnoncesForUser: state => {
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.quotaData)
        ? state.dashboardCache.data.quotaData.totalAnnoncesForUser || 0
        : 0
    },

    getUserQuotaPerPortal: state => {
      return (state.dashboardCache && state.dashboardCache.data) ? state.dashboardCache.data.userQuota : null
    },

    getPortalCodeName: state => {
      return (state.portal && state.portal.infos && state.portal.infos._embedded && state.portal.infos._embedded.diffuseur)
        ? state.portal.infos._embedded.diffuseur.nom_de_code
        : null
    },

    // Getters pour promoteurs
    getNbProgrammesTotalUser: state => {
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.promoteurData)
        ? state.dashboardCache.data.promoteurData.nbProgrammesTotalUser || 0
        : 0
    },

    getNbLotsTotalUser: state => {
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.promoteurData)
        ? state.dashboardCache.data.promoteurData.nbLotsTotalUser || 0
        : 0
    },

    getNbProgrammesActifsUser: state => {
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.promoteurData)
        ? state.dashboardCache.data.promoteurData.nbProgrammesActifsUser || 0
        : 0
    },

    getNbLotsActifsUser: state => {
      return (state.dashboardCache && state.dashboardCache.data && state.dashboardCache.data.promoteurData)
        ? state.dashboardCache.data.promoteurData.nbLotsActifsUser || 0
        : 0
    },

    // Getter pour les champs de formulaire (compatibilité)
    getPostField: state => getField(state.post || {})
  },

  //  ACTIONS ULTRA-SIMPLIFIÉES
  actions: {
    async loadCompleteDashboard({ commit, state, rootGetters }, { portalId, additionalFilters = {} }) {
      const startTime = performance.now()

      try {
        commit('setLoading', true)
        commit('clearError')
        const filters = {
          ...state.filter,
          ...additionalFilters,
          userId: rootGetters.getUser && rootGetters.getUser.id,
          userType: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.metier,
          sitimmoUserId: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.id,
          univers: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.univers,
          advertiser: rootGetters.getUser && rootGetters.getUser.advertiser
        }

        const response = await HTTP_HYPER_ESPACE_BACKEND.get(`/api/broadcast/${portalId}/dashboard`, {
          params: filters
        })

        if (response.data.success) {
          // Mise à jour atomique de tout l'état
          commit('setDashboardData', response.data)

          const executionTime = Math.round(performance.now() - startTime)
          console.info(` Dashboard optimisé chargé en ${executionTime}ms (gain: ${Math.round((1 - executionTime/5000) * 100)}%)`)

          return { success: true, data: response.data.data }
        } else {
          throw new Error(response.data.error || 'Erreur lors du chargement')
        }

      } catch (error) {
        console.error(' Erreur dashboard optimisé:', error)
        commit('setError', (error.response && error.response.data && error.response.data.message) ? error.response.data.message : error.message)
        return { success: false, error: error.message }

      } finally {
        commit('setLoading', false)
      }
    },



    async activateBroadcast({ commit }, { portalId, selected, activate, options = {} }) {
      const startTime = performance.now()

      try {
        commit('setLoadingActivation', true)
        commit('clearError')

        const payload = {
          selected,
          activate,
          checkLots: options.checkLots || false,
          isPromoteur: options.isPromoteur || false,
          isBailleurSocial: options.isBailleurSocial || false,
          portalCode: options.portalCode || '',
          annonceParente: options.annonceParente
        }

        const response = await HTTP_HYPER_ESPACE_BACKEND.post(`/api/broadcast/${portalId}/activate`, payload)

        if (response.data.success) {
          const executionTime = Math.round(performance.now() - startTime)
          const processedCount = response.data.data.processed_count || selected.length

          console.info(` Activation optimisée: ${processedCount} éléments en ${executionTime}ms`)

          return {
            success: true,
            data: response.data.data,
            performance: { executionTime, processedCount }
          }
        } else {
          throw new Error(response.data.error)
        }

      } catch (error) {
        console.error(' Erreur activation optimisée:', error)
        commit('setError', (error.response && error.response.data && error.response.data.message) ? error.response.data.message : error.message)
        return { success: false, error: error.message }

      } finally {
        commit('setLoadingActivation', false)
      }
    },
    async loadUserQuotas({ commit }, { portalId, userId }) {
      try {
        const response = await HTTP_HYPER_ESPACE_BACKEND.get(`/api/broadcast/${portalId}/quotas`, {
          params: { userId }
        })

        if (response.data.success) {
          return { success: true, data: response.data.data }
        } else {
          throw new Error(response.data.error)
        }

      } catch (error) {
        console.error('Erreur quotas utilisateur:', error)
        commit('setError', (error.response && error.response.data && error.response.data.message) ? error.response.data.message : error.message)
        return { success: false, error: error.message }
      }
    },
    async handleSortChange({ commit, state }, { column, portalId }) {
      if (column.prop === 'col_ref') {
        const sortValue = column.order === 'ascending' ? 'R' : (column.order === 'descending' ? '-R' : '-DC')
        commit('setSort', sortValue)
      } else if (column.prop === 'col_price') {
        const sortValue = column.order === 'ascending' ? 'P' : (column.order === 'descending' ? '-P' : '-DC')
        commit('setSort', sortValue)
      } else if (column.prop === 'col_diff') {
        // Pour le tri par sélection, on ajoute aussi linkedPortals
        const linkedPortals = column.order ? [portalId] : null
        const sortValue = column.order === 'ascending' ? 'APA' : (column.order === 'descending' ? '-APA' : '-DC')

        // Mettre à jour les filtres
        commit('addFilter', { linkedPortals })

        commit('setSort', sortValue)
      }

      // Reset de la page à 1 lors du tri
      commit('setPage', 1)
    },

    async loadQuotaPerPortal({ commit }, { idUser, idPortail }) {
      try {
        // Cette action est maintenant obsolète - les quotas sont chargés via loadCompleteDashboard
        console.warn('loadQuotaPerPortal est obsolète, utilisez loadCompleteDashboard')
        return { success: false, error: 'Action obsolète' }
      } catch (error) {
        console.error('Erreur loadQuotaPerPortal:', error)
        return { success: false, error: error.message }
      }
    },
    async loadPosts({ commit, state }, { portalId, filters = {} }) {
      try {
        commit('setLoading', true)
        commit('clearError')

        const mergedFilters = { ...state.filter, ...filters }

        const response = await HTTP_HYPER_ESPACE_BACKEND.get(`/api/broadcast/${portalId}/posts`, {
          params: mergedFilters
        })

        if (response.data.success) {
          const posts = response.data.data
          commit('setResults', posts)
          return { success: true, data: posts }
        } else {
          throw new Error(response.data.error || 'Erreur lors du chargement des annonces')
        }

      } catch (error) {
        console.error(' Erreur chargement annonces:', error)
        commit('setError', error.message)
        return { success: false, error: error.message }

      } finally {
        commit('setLoading', false)
      }
    },

    // Actions simplifiées pour le composant
    async initializeBroadcast({ commit, dispatch, rootGetters }, { portalId }) {
      try {
        // Initialisation du filtre
        commit('resetFilter')
        commit('addFilter', { annonceParente: null })

        // Préparation des filtres avec logique utilisateur
        const additionalFilters = {
          userId: rootGetters.getUser && rootGetters.getUser.id,
          userType: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.metier,
          sitimmoUserId: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.id,
          univers: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.univers,
          advertiser: rootGetters.getUser && rootGetters.getUser.advertiser
        }

        //  CORRECTION CRITIQUE : Ajout du filtrage utilisateur au chargement initial
        // Reproduction de la même logique que buildFiltersAndLoadPosts
        const user = rootGetters.getUser

        if (rootGetters.isSubUser && user && user.identifierCode && user.identifierValue) {
          additionalFilters.identifierCode = user.identifierCode
          additionalFilters.identifierValue = user.identifierValue
          additionalFilters.isSubUser = true
        }

        // Chargement du dashboard complet
        const result = await dispatch('loadCompleteDashboard', {
          portalId,
          additionalFilters
        })
        return result
      } catch (error) {
        console.error('Erreur initialization broadcast:', error)
        return { success: false, error: error.message }
      }
    },

    async refreshAfterActivation({ dispatch }, { portalId, userFilters = {} }) {
      try {
        // Simple rechargement du dashboard après activation
        return await dispatch('loadCompleteDashboard', {
          portalId,
          additionalFilters: userFilters
        })
      } catch (error) {
        console.error('Erreur refresh après activation:', error)
        return { success: false, error: error.message }
      }
    },

    async buildFiltersAndLoadPosts({ commit, dispatch, state, rootGetters }, { portalId, portalCodeName, isPromoteur }) {
      try {
        const additionalFilters = { ...state.filter }

        // Gestion des annonces vendues
        if (portalCodeName && portalCodeName.search('BIENS_VENDUS') >= 0) {
          additionalFilters.annoncesVendues = true
          additionalFilters.annoncesActivees = null
        }

        // Filtrage pour promoteurs
        if (isPromoteur && !state.filter.annonceParente) {
          additionalFilters.typeObjets = 3000
        }

        // Ajout des infos utilisateur
        additionalFilters.userId = rootGetters.getUser && rootGetters.getUser.id
        additionalFilters.userType = rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.metier
        additionalFilters.sitimmoUserId = rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.id
        additionalFilters.univers = rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.univers
        additionalFilters.advertiser = rootGetters.getUser && rootGetters.getUser.advertiser

        //  CORRECTION CRITIQUE : Ajout des identifiants utilisateur pour le filtrage
        // Reproduction de la logique posts.js ligne 1267-1269 et postsVo.js ligne 332-335
        const user = rootGetters.getUser
        if (rootGetters.isSubUser && user && user.identifierCode && user.identifierValue) {
          additionalFilters.identifierCode = user.identifierCode
          additionalFilters.identifierValue = user.identifierValue
          additionalFilters.isSubUser = true
        }

        // Utiliser la nouvelle API optimisée
        return await dispatch('loadPosts', {
          portalId,
          filters: additionalFilters
        })
      } catch (error) {
        console.error('Erreur build filters and load posts:', error)
        return { success: false, error: error.message }
      }
    },

    // ✨ ACTION ULTRA-CONSOLIDÉE pour activation/désactivation avec UI
    async handleActivationWithUI({ dispatch, getters, rootGetters }, {
      portalId,
      selected,
      activate,
      checkLots,
      isPromoteur,
      isBailleurSocial,
      annonceParente,
      messageHandler,
      messages
    }) {
      try {
        // 1. Validation
        const portalCode = getters.getPortalCodeName
        if (!portalCode) {
          const errorMsg = 'Code du portail manquant - impossible de procéder à l\'activation'
          messageHandler({ message: errorMsg, type: 'error' })
          return { success: false, error: errorMsg }
        }

        // 2. Appel d'activation
        const result = await dispatch('activateBroadcast', {
          portalId,
          selected,
          activate,
          options: {
            checkLots,
            isPromoteur,
            isBailleurSocial,
            portalCode,
            annonceParente
          }
        })

        // 3. Gestion des résultats avec UI
        if (result.success) {
          // Rechargement automatique
          await dispatch('refreshAfterActivation', {
            portalId,
            userFilters: {
              userId: rootGetters.getUser && rootGetters.getUser.id,
              userType: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.metier,
              sitimmoUserId: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.id,
              univers: rootGetters.getSitimmoUser && rootGetters.getSitimmoUser.univers,
              advertiser: rootGetters.getUser && rootGetters.getUser.advertiser
            }
          })

          // Message de succès (déjà traduit)
          const successMsg = activate ? messages.success : messages.successDeactivation
          messageHandler({ message: successMsg, type: 'success' })

          return { success: true, cleared: true } // Signal pour vider la sélection
        } else {
          const errorMsg = result.error || `Erreur lors de ${activate ? 'l\'activation' : 'la désactivation'}`
          messageHandler({ message: errorMsg, type: 'error' })
          return { success: false, error: errorMsg }
        }

      } catch (error) {
        const errorMsg = error.message || `Erreur lors de ${activate ? 'l\'activation' : 'la désactivation'}`
        console.error('Erreur activation/désactivation:', error)
        messageHandler({ message: errorMsg, type: 'error' })
        return { success: false, error: errorMsg }
      }
    },

    // Action pour la gestion des programmes/lots
    async handleProgramNavigation({ commit, dispatch }, { id = null, portalId, portalCodeName, isPromoteur }) {
      try {
        // Reset de la page
        commit('setPage', 1)

        // Configuration du filtre pour programme ou retour
        const filterUpdate = id
          ? { page: 1, offset: 0, annonceParente: id }  // Navigation vers programme
          : { annonceParente: null }                     // Retour aux programmes

        commit('setFilter', { ...filterUpdate })

        // Rechargement de la liste
        return await dispatch('buildFiltersAndLoadPosts', {
          portalId,
          portalCodeName,
          isPromoteur
        })
      } catch (error) {
        console.error('Erreur navigation programme:', error)
        return { success: false, error: error.message }
      }
    }

  },
  mutations: {
    // Mutations de base
    setLoading(state, loading) {
      state.loading = loading
    },

    setLoadingActivation(state, loading) {
      state.loadingActivation = loading
    },

    setError(state, error) {
      state.lastError = error
    },

    clearError(state) {
      state.lastError = null
    },
    setDashboardData(state, response) {


      state.dashboardCache = response



      // Mise à jour des données principales
      if (response.data && response.data.portal) {
        state.portal = response.data.portal
      }

      // Extraction intelligente des données selon la structure
      if (response.data) {

        let results = null

        // Option 1: Dashboard renvoie les posts directement (data.total + data.list)
        if (response.data.total !== undefined && response.data.list !== undefined) {
          results = response.data

        }
        // Option 2: Dashboard renvoie les posts dans data.posts
        else if (response.data.posts && response.data.posts.total !== undefined) {
          results = response.data.posts

        }
        // Option 3: Dashboard renvoie les posts dans data.results
        else if (response.data.results && response.data.results.total !== undefined) {
          results = response.data.results

        }
        // Option 4: Chercher d'autres propriétés possibles
        else {

          // Parcourir toutes les propriétés pour trouver celle qui contient total + list
          for (const key in response.data) {
            if (response.data[key] && typeof response.data[key] === 'object' &&
                response.data[key].total !== undefined && response.data[key].list !== undefined) {
              results = response.data[key]
              break
            }
          }

          if (!results) {
            results = { total: 0, count: 0, list: [] }
          }
        }

        state.results = results && typeof results === 'object'
          ? {
              total: results.total || 0,
              count: results.count || results.total || 0,
              list: Array.isArray(results.list) ? results.list : []
            }
          : { total: 0, count: 0, list: [] }
      }
    },

    // Mutations de filtres
    setFilter(state, filter) {
      state.filter = { ...getDefaultFilter(), ...filter }
    },

    setPage(state, page) {
      state.filter = { ...state.filter, page }
    },

    setLimit(state, limit) {
      state.filter = { ...state.filter, limit, page: 1 }
    },

    setSort(state, sort) {
      state.filter = { ...state.filter, sort, page: 1 }
    },

    setSearch(state, search) {
      state.filter = { ...state.filter, search, page: 1 }
    },

    addFilter(state, filter) {
      state.filter = { ...state.filter, ...filter, page: 1 }
    },

    setOffset(state, offset) {
      state.filter = { ...state.filter, offset }
    },

    resetFilter(state) {
      state.filter = getDefaultFilter()
      state.results = { total: -1, list: [] }
    },

    resetPresetFilters(state) {
      state.filter = {
        ...getDefaultFilter(),
        advanced: state.filter.advanced,
        ...getPresetFilters()
      }
    },

    // Mutation pour les résultats
    setResults(state, results) {
      // Vérification défensive pour s'assurer que results est toujours un objet valide
      state.results = results && typeof results === 'object'
        ? {
            total: results.total || 0,
            count: results.count || 0,
            list: Array.isArray(results.list) ? results.list : []
          }
        : { total: 0, count: 0, list: [] }
    },

    // Mutations pour la compatibilité (si nécessaire)
    setPortal(state, portal) {
      state.portal = portal
    },

    // Mutation pour les champs de formulaire
    updatePostField(state, field) {
      if (!state.post) state.post = {}
      updateField(state.post, field)
    }
  }
}

